import { suggestConstituentAssignments } from './lobbyScheduleResolver'
import { DAY_START_MINUTES, DAY_END_MINUTES, minutesToTime } from './lobbyScheduleUtils'
import type {
  ScheduleAttendeeDto, ScheduleLegislatorRowDto, ScheduleMeetingDto,
} from '../../../types/lobbyDay'
import { uuid } from '../../../lib/uuid'

export interface ItineraryTargetInput {
  legislatorId: string
  isPriority: boolean
  officeRoom?: string | null
}

export interface AutoBuildSummary {
  createdCount: number
  assignedCount: number
  gapCount: number
  unplacedCount: number
}

interface BuildItineraryOptions {
  durationMin: number
  dayStartMin?: number
  dayEndMin?: number
}

// Local copy of the addDays helper duplicated in LobbyScheduler/AgendaEditor (pre-existing dup, out of scope to unify here).
function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Inclusive list of event days from startDate..endDate ("" start => []). */
export function eventDays(startDate: string, endDate: string | null): string[] {
  if (!startDate) return []
  const days = [startDate]
  if (endDate && endDate !== startDate) {
    let d = startDate
    while (d < endDate) {
      d = addDays(d, 1)
      days.push(d)
    }
  }
  return days
}

/** Create a meeting per effective-target legislator without one, placed sequentially in time
 *  (priority-first) across the event days. Never mutates or duplicates existing meetings. */
export function buildPreliminaryItinerary(
  targets: ItineraryTargetInput[],
  existingMeetings: ScheduleMeetingDto[],
  days: string[],
  opts: BuildItineraryOptions,
): { meetings: ScheduleMeetingDto[]; created: ScheduleMeetingDto[]; unplaced: string[] } {
  const dayStart = opts.dayStartMin ?? DAY_START_MINUTES
  const dayEnd = opts.dayEndMin ?? DAY_END_MINUTES
  const duration = opts.durationMin

  const hasMeeting = new Set(existingMeetings.map((m) => m.legislatorId))
  // Stable priority-first order (input is already priority-first from coverage; this is belt-and-suspenders).
  const candidates = targets
    .filter((t) => !hasMeeting.has(t.legislatorId))
    .map((t, i) => ({ t, i }))
    .sort((a, b) => (Number(b.t.isPriority) - Number(a.t.isPriority)) || (a.i - b.i))
    .map((x) => x.t)

  const created: ScheduleMeetingDto[] = []
  const unplaced: string[] = []

  let dayIndex = 0
  let slotStart = dayStart
  for (const t of candidates) {
    // advance to a day/slot that fits a full `duration`
    while (dayIndex < days.length && slotStart + duration > dayEnd) {
      dayIndex += 1
      slotStart = dayStart
    }
    if (dayIndex >= days.length) {
      unplaced.push(t.legislatorId)
      continue
    }
    created.push({
      id: `new:${uuid()}`,
      legislatorId: t.legislatorId,
      askId: null,
      askTitle: null,
      meetingDate: days[dayIndex],
      startTime: minutesToTime(slotStart),
      endTime: minutesToTime(slotStart + duration),
      room: t.officeRoom ?? null,
      status: 'Confirmed',
      withWhom: 'Unknown',
      packetNotes: null,
      notes: null,
      validity: 'Empty',
      assignments: [],
      askResponse: 'NotRecorded',
      responseNotes: null,
      attendeeReportCount: 0,
      staff: [],
    })
    slotStart += duration
  }

  return { meetings: [...existingMeetings, ...created], created, unplaced }
}

/** Full auto-build: place meetings, then fill them (and any existing meetings) with conflict-free
 *  constituents via the existing resolver. Returns the staged meeting list + a summary for the toast. */
export function composeAutoBuild(
  targets: ItineraryTargetInput[],
  existingMeetings: ScheduleMeetingDto[],
  attendees: ScheduleAttendeeDto[],
  legislators: ScheduleLegislatorRowDto[],
  days: string[],
  durationMin: number,
): { meetings: ScheduleMeetingDto[]; summary: AutoBuildSummary } {
  const built = buildPreliminaryItinerary(targets, existingMeetings, days, { durationMin })
  const filled = suggestConstituentAssignments(built.meetings, attendees, legislators)
  const createdIds = new Set(built.created.map((m) => m.id))
  const gapCount = filled.meetings.filter(
    (m) => createdIds.has(m.id) && !m.assignments.some((a) => a.constituency === 'Constituent'),
  ).length
  return {
    meetings: filled.meetings,
    summary: {
      createdCount: built.created.length,
      assignedCount: filled.stagedCount,
      gapCount,
      unplacedCount: built.unplaced.length,
    },
  }
}
